import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Country, Customer, PayableLocationsGet, PaymentPlan, PromotionCodeDetailsGet, PromotionCodeDetailsRequest, SubscriptionCostGet, SubscriptionCreate, SubscriptionGet } from '../../../core/interfaces/payment-interfaces';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CustomDropdownComponent } from '../../components/custom-dropdown/custom-dropdown.component';
import { LabeledInputComponent } from '../../components/labeled-input/labeled-input.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PaymentService } from '../../../core/api/payment.service';
import { ApiErrorService } from '../../../core/api/api-error.service';
import { ToastComponent } from '../../components/toast/toast.component';
import { CustomButtonComponent } from "../../components/custom-button/custom-button.component";
import { AuthValidatorService } from '../../../core/services/validators/auth-validator.service';
import { CommonValidatorService } from '../../../core/services/validators/common-validator.service';
import { lastValueFrom } from 'rxjs';
import { PaymentComponent } from "../../components/payment/payment.component";
import { Router, RouterLink } from '@angular/router';
import { UserDataService } from '../../../core/services/user-data.service';
import { PaymentMethodUpdateComponent } from "../../../features/settings/components/payment-method-update/payment-method-update.component";

@Component({
  selector: 'app-payment-dialog',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, CustomDropdownComponent, LabeledInputComponent, CustomButtonComponent, PaymentComponent, PaymentMethodUpdateComponent, RouterLink],
  templateUrl: './payment-dialog.component.html',
  styleUrl: './payment-dialog.component.css'
})
export class PaymentDialogComponent {
  modalDialogId: string = 'shared_modal_payment'

  userNeedsToPay: boolean = false;
  subscriptionGet?: SubscriptionGet = undefined;
  selectedPaymentPlan?: PaymentPlan;

  planPayOptionSelected: number | undefined;
  planPayOptions: string[] = [];

  // Text inputs
  billingEmail: string = '';
  billingEmailError: string | null = null;

  billingName: string = '';
  billingNameError: string | null = null;

  billingCity: string = '';
  billingCityError: string | null = null;

  countriesOptions: Country[] = [];
  countriesDropdownOptions: string[] = [];
  selectedCountry: number | undefined;
  selectedCountryError: string | null = null;

  billingAddress: string = '';
  billingAddressError: string | null = null;

  billingPostalCode: string = '';
  billingPostalCodeError: string | null = null;

  billingState: string = '';
  billingStateError: string | null = null;

  billingPecEmailAddress: string = '';
  billingPecEmailAddressError: string | null = null;

  billingSdi: string = '';
  billingSdiError: string | null = null;

  billingVatId: string = '';
  billingVatIdError: string | null = null;

  // Customer
  customerData?: Customer;
  customerDataLoaded: boolean = false;
  customerExists: boolean = true;

  // Continue
  continueClicked: boolean = false;
  openCustomerPanelButtonClicked: boolean = false;

  // Active subscription
  activeSubscription: SubscriptionGet | undefined = undefined;
  activeSubscriptionLoaded: boolean = false;

  // Locations
  payableLocationsData?: PayableLocationsGet;

  // Show price summary
  showPriceSummary: boolean = false;

  // Toast
  @Input() toast!: ToastComponent;

  // Billing Information Missing Error
  showBillingInformationMissingError: boolean = false;

  // Promotion data
  promotionData: PromotionCodeDetailsGet | undefined = undefined;
  promotionDataLoaded: boolean = false;

  formatPrice(price: number | undefined): string {
    return (price ?? 0).toFixed(2);
  }

  // Total cost calculations
  get numberOfLocations(): number {
    return this.calculateNumberOfLocations();
  }

  get pricePerLocation(): number {
    return this.calculatePricePerLocation();
  }

  get getPriceForLocations(): number {
    return (this.numberOfLocations * this.pricePerLocation);
  }

  get subtotal(): number {
    return this.calculateSubtotal();
  }

  get tax(): number {
    return this.calculateTax();
  }

  get getTotalPrice(): number {
    return this.calculateTotalPrice();
  }

  get getDueToday(): number {
    return this.calculateDueToday();
  }

  get isInputDisabled(): boolean {
    return this.activeSubscription != undefined;
  }

  loadPriceData: SubscriptionCostGet | undefined = undefined;

  get isUserOnTrial(): boolean {
    if (!this.userData.getUser() || !this.userData.getUser()?.membership || !this.userData.getUser()?.membership?.valid_to) {
      return false;
    }

    const validToDate: number = new Date(this.userData.getUser()!.membership!.valid_to!).getTime();
    const currentDate: number = Date.now();

    return this.userData.getUser()?.membership?.membership_type === 'TRIAL' && validToDate > currentDate;
  }

  get userPromotionCode(): string | null {
    return this.userData.getUser()?.membership?.auto_promotion_code || null;
  }

  get isEligibleForTrial(): boolean {
    return this.userData.getUser()?.membership?.is_eligible_for_trial || false;
  }

  constructor(private translocoService: TranslocoService, private paymentService: PaymentService, private apiError: ApiErrorService, private authValidator: AuthValidatorService, private commonValidator: CommonValidatorService, private router: Router, private userData: UserDataService) { }

  openModal(selectedPaymentPlan: PaymentPlan, billedYearly: boolean): void {
    const modal = document.getElementById(this.modalDialogId) as HTMLDialogElement;
    if (modal) {
      modal.showModal();

      this.selectedPaymentPlan = selectedPaymentPlan;

      this.planPayOptions = [];
      this.planPayOptions = [`${this.translocoService.translate('settings.billed_yearly') ?? 'Billed Yearly'} - ${this.formatPrice(selectedPaymentPlan.price_per_year)}€`,
      `${this.translocoService.translate('settings.billed_monthly') ?? 'Billed Monthly'} - ${this.formatPrice(selectedPaymentPlan.price_per_month)}€`]
      this.planPayOptionSelected = billedYearly ? 0 : 1;

      this.resetCustomerInputErrors();
      this.resetCustomerInput();

      this.loadCountryCodes();
      this.loadSubscriptionData();
      this.loadPromotionData();
      this.loadPayableLocations();
    }
  }

  closeModal(): void {
    const modal = document.getElementById(this.modalDialogId) as HTMLDialogElement;
    if (modal) {
      modal.close();
    }
  }

  loadCountryCodes() {
    this.paymentService.getCountryCodes().subscribe({
      next: (response) => {
        if (response) {
          this.countriesOptions = response;
          this.countriesDropdownOptions = response.map(c => c.name ? `${c.name} (${c.code})` : c.code);
          this.loadCustomerData();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {
      }
    });
  }

  loadCustomerData() {
    this.paymentService.getCustomer().subscribe({
      next: (response) => {
        if (response) {
          this.customerData = response;
          this.populateCustomerInputs(this.customerData);
          this.customerDataLoaded = true;
          this.validateCustomerInputs();
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

          // Customer does not exist yet
          if (error.error.code == 'SVC-4004') {
            this.customerExists = false;
            this.showBillingInformationMissingError = true;
            showError = false;
          }
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        this.customerDataLoaded = true;
      },
      complete: () => {
      }
    });
  }

  populateCustomerInputs(customerData?: Customer): void {
    if (!customerData) return;

    this.billingEmail = customerData.email || '';
    this.billingName = customerData.name || '';
    this.billingCity = customerData.city || '';
    this.selectedCountry = this.countriesOptions.findIndex(c => c.code === customerData.country);
    if (this.selectedCountry === -1) this.selectedCountry = undefined;
    this.billingAddress = customerData.address || '';
    this.billingPostalCode = customerData.postal_code || '';
    this.billingState = customerData.state || '';
    this.billingPecEmailAddress = customerData.pec_email_address || '';
    this.billingSdi = customerData.sdi || '';
    this.billingVatId = customerData.vat_id || '';
  }

  getCustomerFromInputs(): Customer {
    return {
      id: this.customerData?.id ?? '',
      email: this.billingEmail.trim(),
      name: this.billingName.trim(),
      city: this.billingCity.trim(),
      country: this.selectedCountry !== undefined ? this.countriesOptions[this.selectedCountry].code : '',
      address: this.billingAddress.trim(),
      postal_code: this.billingPostalCode.trim(),
      state: this.billingState.trim(),
      pec_email_address: this.billingPecEmailAddress.trim(),
      sdi: this.billingSdi.trim(),
      vat_id: this.billingVatId.trim(),
    };
  }

  resetCustomerInputErrors(): void {
    this.billingEmailError = null;
    this.billingNameError = null;
    this.billingCityError = null;
    this.selectedCountryError = null;
    this.billingAddressError = null;
    this.billingPostalCodeError = null;
    this.billingStateError = null;
    this.billingPecEmailAddressError = null;
    this.billingSdiError = null;
    this.billingVatIdError = null;

    this.showBillingInformationMissingError = false;
  }

  resetCustomerInput(): void {
    this.billingEmail = '';
    this.billingName = '';
    this.billingCity = '';
    this.selectedCountry = undefined;
    this.billingAddress = '';
    this.billingPostalCode = '';
    this.billingState = '';
    this.billingPecEmailAddress = '';
    this.billingSdi = '';
    this.billingVatId = '';
  }

  async onContinueClick() {
    if (this.continueClicked || !this.customerDataLoaded || this.planPayOptionSelected === undefined || !this.selectedPaymentPlan || !this.activeSubscriptionLoaded || this.activeSubscription != undefined) {
      return;
    }
    this.continueClicked = true;

    if (!await this.createOrUpdateCustomer()) {
      this.continueClicked = false;
      return;
    }

    // Create subscription
    const subscriptionData: SubscriptionCreate = {
      payment_plan_id: this.selectedPaymentPlan.id,
      pay_yearly: this.planPayOptionSelected === 0 ? true : false
    };

    try {
      this.subscriptionGet = await lastValueFrom(this.paymentService.createSubscription(subscriptionData));

      if (this.subscriptionGet?.stripe_subscription_id) {
        this.loadPriceData = await lastValueFrom(this.paymentService.getSubscriptionCost(this.subscriptionGet?.stripe_subscription_id))
      }

      this.userNeedsToPay = true;
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError: boolean = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

        if (error.error.code === 'SVC-4008') {
          this.toast.showToast('shared.error', 'shared.toast.already_have_subscription', 'error', 4000, true);
          showError = false;
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.continueClicked = false;
      return;
    }

    this.continueClicked = false;
  }

  validateCustomerInputs(): boolean {
    const billingEmailValue = this.billingEmail.trim();
    const billingNameValue = this.billingName.trim();
    const billingCityValue = this.billingCity.trim();
    const billingAddressValue = this.billingAddress.trim();
    const billingPostalCodeValue = this.billingPostalCode.trim();
    const billingStateValue = this.billingState.trim();
    const billingPecEmailAddressValue = this.billingPecEmailAddress.trim();
    const billingSdiValue = this.billingSdi.trim();
    const billingVatIdValue = this.billingVatId.trim();

    this.billingEmailError = this.authValidator.validateEmail(billingEmailValue);
    this.billingNameError = this.commonValidator.validateLength(billingNameValue, 2, 255);
    this.billingCityError = this.commonValidator.validateLength(billingCityValue, 2, 255);
    this.billingAddressError = this.commonValidator.validateLength(billingAddressValue, 2, 255);
    this.billingPostalCodeError = this.commonValidator.validateLength(billingPostalCodeValue, 2, 255);
    this.billingStateError = this.commonValidator.validateLength(billingStateValue, 2, 255);
    this.billingPecEmailAddressError = this.commonValidator.validateLength(billingPecEmailAddressValue, 2, 255);
    this.billingSdiError = this.commonValidator.validateLength(billingSdiValue, 2, 255);
    this.billingVatIdError = this.commonValidator.validateLength(billingVatIdValue, 2, 255);

    if (this.selectedCountry === undefined) {
      this.selectedCountryError = this.translocoService.translate('shared.validators.common.required');
    }

    if (
      this.billingEmailError || this.billingNameError || this.billingCityError ||
      this.billingAddressError || this.billingPostalCodeError || this.billingStateError ||
      this.billingPecEmailAddressError || this.billingSdiError || this.billingVatIdError || this.selectedCountryError) {

      this.showBillingInformationMissingError = true;
      return false;
    }
    return true;
  }

  async createOrUpdateCustomer(): Promise<boolean> {
    this.resetCustomerInputErrors();

    if (!this.validateCustomerInputs()) {
      return false;
    }

    const newCustomer = this.getCustomerFromInputs();

    try {
      if (this.customerExists) {
        // Update data if changed
        if (JSON.stringify(this.customerData) !== JSON.stringify(newCustomer)) {
          await lastValueFrom(this.paymentService.updateCustomer(newCustomer));
        }
      }
      else {
        // Create customer
        await lastValueFrom(this.paymentService.createCustomer(newCustomer));
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return false;
    }
    return true;
  }

  loadSubscriptionData() {
    this.activeSubscription = undefined;
    this.activeSubscriptionLoaded = false;

    this.paymentService.getActiveSubscription().subscribe({
      next: (response) => {
        if (response) {
          this.activeSubscription = response;

          this.loadSubscriptionUpdatePrice();
        }
        this.activeSubscriptionLoaded = true;
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          if (error.error.code == 'SVC-4005') {
            showError = false;
            this.showPriceSummary = true;
          }
        }
        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        this.activeSubscriptionLoaded = true;
      },
      complete: () => {
      }
    });
  }

  loadPromotionData() {
    if (!this.userPromotionCode) {
      return;
    }

    this.promotionData = undefined;
    this.promotionDataLoaded = false;

    var request: PromotionCodeDetailsRequest = {
      'promotion_code': this.userPromotionCode
    };

    this.paymentService.getPromotionDetails(request).subscribe({
      next: (response) => {
        if (response) {
          this.promotionData = response;
        }
        this.promotionDataLoaded = true;
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
        this.promotionDataLoaded = true;
      },
      complete: () => {
      }
    });
  }

  async onChangePlanClick() {
    if (this.continueClicked || !this.customerDataLoaded || this.planPayOptionSelected === undefined || !this.selectedPaymentPlan || !this.activeSubscriptionLoaded || this.activeSubscription == undefined) {
      return;
    }
    this.continueClicked = true;

    if (!await this.createOrUpdateCustomer()) {
      this.continueClicked = false;
      return;
    }

    // Create subscription
    const subscriptionData: SubscriptionCreate = {
      payment_plan_id: this.selectedPaymentPlan.id,
      pay_yearly: this.planPayOptionSelected === 0 ? true : false
    };

    try {
      this.subscriptionGet = await lastValueFrom(this.paymentService.updateSubscriptionPlan(subscriptionData));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError: boolean = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

        if (error.error.code === 'SVC-4011') {
          this.toast.showToast('shared.error', 'settings.subscription.already_subscribed', 'error', 4000, true);
          showError = false;
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.continueClicked = false;
      return;
    }


    setTimeout(() => {
      this.continueClicked = false;
      this.router.navigate(['/settings/billing'], { queryParams: { redirect_status: 'succeeded' } });
    }, 4000)
  }

  calculateSelectedPlanPrice(): number {
    return this.planPayOptionSelected === 0 ? this.selectedPaymentPlan?.price_per_year ?? 0 : this.selectedPaymentPlan?.price_per_month ?? 0;
  }

  calculateNumberOfLocations(): number {
    if (this.payableLocationsData) {
      return this.payableLocationsData.extra_locations;
    }
    return 0;
  }
  calculatePricePerLocation(): number {
    if (this.payableLocationsData) {
      return this.planPayOptionSelected === 0 ? this.payableLocationsData.location_plan.price_per_year || 0 : this.payableLocationsData.location_plan.price_per_month || 0;
    }
    return 0;
  }

  calculateSubtotal(): number {
    if (this.loadPriceData) {
      if (this.activeSubscription == undefined) {
        return this.getPriceForLocations + this.calculateSelectedPlanPrice();
      }

      return this.getPriceFromStripe(this.loadPriceData.subtotal);
    }

    return this.getPriceForLocations + this.calculateSelectedPlanPrice();
  }

  calculateTax(): number {
    if (this.loadPriceData) {
      return this.getPriceFromStripe(this.loadPriceData.tax_amount);
    }
    return 0;
  }

  calculateTotalPrice(): number {
    if (this.loadPriceData) {
      if (this.activeSubscription == undefined) {
        return this.calculateSubtotal() + this.calculateTax();
      }

      return this.getPriceFromStripe(this.loadPriceData.total);
    }
    return this.calculateSubtotal() + this.calculateTax();
  }

  calculateDueToday(): number {
    if (this.loadPriceData) {
      return this.getPriceFromStripe(this.loadPriceData.total);
    }
    return this.calculateTotalPrice();
  }

  getPriceFromStripe(price: number): number {
    return (price / 100);
  }

  changeSubscriptionUpdatePrice() {
    if (this.activeSubscription != undefined) {
      this.loadSubscriptionUpdatePrice();
    }
  }

  loadSubscriptionUpdatePrice() {
    if (!this.selectedPaymentPlan) {
      return;
    }

    this.showPriceSummary = false;

    const subscriptionData: SubscriptionCreate = {
      payment_plan_id: this.selectedPaymentPlan.id,
      pay_yearly: this.planPayOptionSelected === 0 ? true : false
    };

    this.loadPriceData = undefined;

    this.paymentService.getSubscriptionUpdateCost(subscriptionData.payment_plan_id, subscriptionData.pay_yearly).subscribe({
      next: (response) => {
        if (response) {
          this.loadPriceData = response;
        }
        this.showPriceSummary = true;
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          if (error.error.code == 'SVC-4011') {
            showError = false;
          }
        }

        this.showPriceSummary = true;

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      },
      complete: () => {
      }
    });
  }

  loadPayableLocations() {
    if (!this.selectedPaymentPlan) {
      return;
    }
    const subscriptionData: SubscriptionCreate = {
      payment_plan_id: this.selectedPaymentPlan.id,
      pay_yearly: this.planPayOptionSelected === 0 ? true : false
    };

    this.payableLocationsData = undefined;

    this.paymentService.getLocationPayable(subscriptionData.payment_plan_id, subscriptionData.pay_yearly).subscribe({
      next: (response) => {
        if (response) {
          this.payableLocationsData = response;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';
        var showError = true;

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }

        if (showError) {
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
        }
      },
      complete: () => {
      }
    });
  }

  async openCustomerPanel() {
    const returnUrl = window.location.href;
    this.openCustomerPanelButtonClicked = true;

    try {
      const response = await lastValueFrom(this.paymentService.getCustomerPortal(returnUrl));

      if (response?.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.error.code == 'SVC-4004') {
          this.toast.showToast("shared.error", "settings.subscription.customer_missing", 'warning', 4000, true);
          showError = false;
        }
        else if (error.error.code == 'SVC-5002') {
          this.toast.showToast("shared.error", "shared.toast.external_service_not_available", 'error', 4000, true);
          showError = false;
        }
      }
      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
      this.openCustomerPanelButtonClicked = false;
      return;
    }
    this.openCustomerPanelButtonClicked = false;
  }

  getPromotionDescription(data: PromotionCodeDetailsGet | null): string {
    if (!data || !data.is_valid) {
      return '';
    }

    // Discount
    let discountPart = '';

    if (data.percent_off != null) {
      discountPart = this.translocoService.translate('settings.promotion.percent_off', {
        percent: data.percent_off
      });
    } else if (data.amount_off != null && data.currency) {
      discountPart = this.translocoService.translate('settings.promotion.amount_off', {
        amount: data.amount_off,
        currency: data.currency.toUpperCase()
      });
    } else {
      discountPart = this.translocoService.translate('settings.promotion.generic_discount');
    }

    // Duration
    let durationPart = '';

    if (data.duration === 'once') {
      durationPart = this.translocoService.translate('settings.promotion.duration_once');
    } else if (data.duration === 'repeating' && data.duration_in_months) {
      durationPart = this.translocoService.translate('settings.promotion.duration_repeating', {
        months: data.duration_in_months
      });
    } else if (data.duration === 'forever') {
      durationPart = this.translocoService.translate('settings.promotion.duration_forever');
    }

    // Applied to plan selected
    const applyPart = this.translocoService.translate('settings.promotion.applies_to_plan');

    // Expiration
    let expirationPart = '';
    if (data.expires_at) {
      const date = new Date(data.expires_at).toLocaleDateString();
      expirationPart = this.translocoService.translate('settings.promotion.valid_until', {
        date
      });
    }

    return [discountPart, durationPart, applyPart, expirationPart].filter(Boolean).join(' ');
  }
}
